// PlatoSyncButton Component
// Button for manually syncing appointments from Plato Medical

import { useState } from 'react';
import { Download, Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { usePlatoSync } from '@/hooks/usePlatoSync';
import PlatoSyncReportModal from '@/components/plato/PlatoSyncReportModal';
import PlatoSyncProgressModal from '@/components/plato/PlatoSyncProgressModal';
import type { PlatoSyncResultItem } from '@/types/plato';

interface PlatoSyncButtonProps {
  /**
   * The type of sync to perform
   * - 'day': Sync appointments for a single day
   * - 'week': Sync appointments for a week
   */
  syncType: 'day' | 'week';

  /**
   * The date to sync
   * - For 'day': The specific date to sync (YYYY-MM-DD)
   * - For 'week': The start date of the week to sync (YYYY-MM-DD)
   */
  date: string;

  /**
   * Optional clinic ID (defaults to Forum clinic)
   */
  clinicId?: string;

  /**
   * Optional size variant for the button
   */
  size?: 'default' | 'sm' | 'lg' | 'icon';

  /**
   * Optional variant for the button
   */
  variant?: 'default' | 'outline' | 'ghost' | 'secondary';

  /**
   * Optional class name for additional styling
   */
  className?: string;

  /**
   * Optional callback when schedule needs refreshing
   */
  onRefreshSchedule?: () => void;
}

export const PlatoSyncButton = ({
  syncType,
  date,
  clinicId,
  size = 'sm',
  variant = 'outline',
  className,
  onRefreshSchedule,
}: PlatoSyncButtonProps) => {
  const [showReportModal, setShowReportModal] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const {
    syncDay,
    syncWeek,
    isSyncingDay,
    isSyncingWeek,
    isAvailable,
    lastSyncResult,
    clearLastSyncResult,
    createConflictItem,
    syncProgress,
    clearSyncProgress,
  } = usePlatoSync({
    clinicId,
    onSuccess: () => {
      // Refresh schedule data immediately after sync so new appointments appear without page reload.
      // This clears lazy-loaded cache ranges and forces a full refetch.
      onRefreshSchedule?.();
      // Show the report modal after sync completes
      setShowReportModal(true);
    },
  });

  const isSyncing = syncType === 'day' ? isSyncingDay : isSyncingWeek;

  const handleClick = () => {
    // Show confirmation dialog before syncing
    setShowConfirmDialog(true);
  };

  const handleConfirmSync = () => {
    setShowConfirmDialog(false);
    if (syncType === 'day') {
      syncDay(date);
    } else {
      syncWeek(date);
    }
  };

  const handleCloseModal = () => {
    setShowReportModal(false);
    clearLastSyncResult();
    clearSyncProgress();
    // Always refresh the schedule after sync to show latest changes
    if (onRefreshSchedule) {
      onRefreshSchedule();
    }
  };

  const handleCreateConflictItem = async (item: PlatoSyncResultItem) => {
    await createConflictItem(item);
  };

  // Don't render if Plato sync is not configured
  if (!isAvailable) {
    return null;
  }

  const buttonLabel = syncType === 'day' ? 'Sync from Plato' : 'Sync Week from Plato';
  const tooltipText = syncType === 'day'
    ? 'Pull appointments from Plato for this day'
    : 'Pull appointments from Plato for this week';

  const syncPeriod = syncType === 'day' ? 'this day' : 'this week';

  return (
    <>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={variant}
              size={size}
              onClick={handleClick}
              disabled={isSyncing}
              className={className}
            >
              {isSyncing ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              {isSyncing ? 'Syncing...' : buttonLabel}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>{tooltipText}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {/* Confirmation Dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Confirm Plato Sync
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                This will sync appointments from Plato for <strong>{syncPeriod}</strong>.
              </p>
              <p className="text-sm text-muted-foreground">
                Plato appointments are refreshed for {syncPeriod}. Your manually
                keyed appointments are kept, and existing nurse (DA) assignments
                are preserved. Breaks, leaves, admin tasks, and MC entries are
                untouched.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmSync}>
              Continue with Sync
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Live progress while syncing (and on error). Auto-closes on 'done',
          after which the report modal opens. */}
      <PlatoSyncProgressModal
        isOpen={!!syncProgress && syncProgress.stage !== 'done'}
        progress={syncProgress}
        onClose={clearSyncProgress}
      />

      <PlatoSyncReportModal
        isOpen={showReportModal}
        onClose={handleCloseModal}
        result={lastSyncResult}
        onCreateConflictItem={handleCreateConflictItem}
        onRefreshSchedule={onRefreshSchedule}
      />
    </>
  );
};

export default PlatoSyncButton;
